// Draggable property type row with conditional inputs per kind.

import { GripVertical, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { PropertyKind } from "@/types/ontology";
import type { ObjectType } from "@/types/ontology";
import { EnumValuesInput } from "./EnumValuesInput";

export interface DraftPropertyType {
  /** Undefined for unsaved rows. */
  id?: string;
  name: string;
  api_name: string;
  kind: PropertyKind;
  required: boolean;
  unique_in_type: boolean;
  enum_values: string[] | null;
  ref_object_type_id: string | null;
  currency_code: string | null;
  description: string | null;
  position: number;
}

const KIND_OPTIONS: { value: PropertyKind; label: string }[] = [
  { value: "string", label: "String" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "bool", label: "Boolean" },
  { value: "currency", label: "Currency" },
  { value: "enum", label: "Enum" },
  { value: "ref", label: "Reference" },
];

interface PropertyTypeRowProps {
  property: DraftPropertyType;
  index: number;
  onChange: (updated: DraftPropertyType) => void;
  onDelete: () => void;
  /** Non-null when delete is blocked due to active mapping reference. */
  deleteError: string | null;
  /** Non-null when the property's api_name violates the naming convention. */
  apiNameError?: string | null;
  /** Other object types for the ref kind dropdown. */
  objectTypes: ObjectType[];
  disabled?: boolean;
  // HTML5 drag-and-drop handlers
  onDragStart: (e: React.DragEvent, index: number) => void;
  onDragOver: (e: React.DragEvent, index: number) => void;
  onDrop: (e: React.DragEvent, index: number) => void;
  onDragEnd: () => void;
  /** Show a drop indicator above this row. */
  isDragTarget: boolean;
}

export function PropertyTypeRow({
  property,
  index,
  onChange,
  onDelete,
  deleteError,
  apiNameError,
  objectTypes,
  disabled,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  isDragTarget,
}: PropertyTypeRowProps) {
  function update(partial: Partial<DraftPropertyType>) {
    onChange({ ...property, ...partial });
  }

  return (
    <div className={cn("relative", isDragTarget && "border-t-2 border-t-primary")}>
      <div
        draggable
        onDragStart={(e) => onDragStart(e, index)}
        onDragOver={(e) => onDragOver(e, index)}
        onDrop={(e) => onDrop(e, index)}
        onDragEnd={onDragEnd}
        className={cn(
          "min-h-12 rounded-md border bg-card/40 flex items-start gap-2 px-2 py-2 hover:bg-card/80 transition-colors",
          disabled && "opacity-60"
        )}
        data-testid={`property-row-${index}`}
      >
        {/* Drag handle */}
        <GripVertical
          className="w-4 h-4 text-muted-foreground cursor-grab flex-shrink-0 mt-1"
          aria-hidden="true"
        />

        {/* Name */}
        <Input
          value={property.name}
          onChange={(e) => update({ name: e.target.value })}
          placeholder="Name"
          size={1}
          className="h-8 w-48 text-sm flex-shrink-0"
          disabled={disabled}
          aria-label="Property name"
        />

        {/* api_name (read-only display) */}
        <span className="font-mono text-xs text-muted-foreground w-40 truncate flex-shrink-0 hidden md:block">
          {property.api_name || "—"}
        </span>

        {/* Kind */}
        <Select
          value={property.kind}
          onValueChange={(v) => update({ kind: v as PropertyKind })}
          disabled={disabled}
        >
          <SelectTrigger className="h-8 w-28 text-sm flex-shrink-0" aria-label="Property kind">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {KIND_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Required */}
        <div className="flex items-center gap-1 w-16 flex-shrink-0" title="Required">
          <Checkbox
            checked={property.required}
            onCheckedChange={(checked) => update({ required: checked === true })}
            disabled={disabled}
            aria-label="Required"
            id={`required-${index}`}
          />
          <label
            htmlFor={`required-${index}`}
            className="text-xs text-muted-foreground cursor-pointer select-none"
          >
            Req
          </label>
        </div>

        {/* Unique */}
        <div className="flex items-center gap-1 w-16 flex-shrink-0" title="Unique in type">
          <Checkbox
            checked={property.unique_in_type}
            onCheckedChange={(checked) => update({ unique_in_type: checked === true })}
            disabled={disabled}
            aria-label="Unique"
            id={`unique-${index}`}
          />
          <label
            htmlFor={`unique-${index}`}
            className="text-xs text-muted-foreground cursor-pointer select-none"
          >
            Uniq
          </label>
        </div>

        {/* Conditional inputs — values column */}
        <div className="flex-1 min-w-0">
          {property.kind === "enum" && (
            <EnumValuesInput
              values={property.enum_values ?? []}
              onChange={(vals) => update({ enum_values: vals.length > 0 ? vals : null })}
              disabled={disabled}
            />
          )}

          {property.kind === "ref" && (
            <Select
              value={property.ref_object_type_id ?? "__none__"}
              onValueChange={(v) => update({ ref_object_type_id: v === "__none__" ? null : v })}
              disabled={disabled}
            >
              <SelectTrigger className="h-8 w-full text-sm min-w-0" aria-label="Reference type">
                <SelectValue placeholder="Select type..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Select type...</SelectItem>
                {objectTypes.map((ot) => (
                  <SelectItem key={ot.id} value={ot.id}>
                    {ot.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {property.kind === "currency" && (
            <Input
              value={property.currency_code ?? ""}
              onChange={(e) => update({ currency_code: e.target.value.toUpperCase() || null })}
              placeholder="USD"
              maxLength={3}
              className="h-8 w-20 text-sm font-mono flex-shrink-0"
              disabled={disabled}
              aria-label="Currency code"
            />
          )}
        </div>

        {/* Description */}
        <Input
          value={property.description ?? ""}
          onChange={(e) => update({ description: e.target.value || null })}
          placeholder="Description"
          className="h-8 w-40 flex-shrink-0 min-w-0 text-xs text-muted-foreground"
          disabled={disabled}
          aria-label="Property description"
        />

        {/* Delete */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 flex-shrink-0 text-muted-foreground hover:text-destructive mt-0.5"
          onClick={onDelete}
          disabled={disabled}
          aria-label="Delete property"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>

      {/* Inline delete error */}
      {deleteError && (
        <p
          className="text-xs text-destructive mt-1 px-2"
          role="alert"
          data-testid={`delete-error-${index}`}
        >
          {deleteError}
        </p>
      )}

      {/* Inline api_name validation error */}
      {apiNameError && (
        <p
          className="text-xs text-destructive mt-1 px-2"
          role="alert"
          data-testid={`api-name-error-${index}`}
        >
          {apiNameError}
        </p>
      )}
    </div>
  );
}
